import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import Input, { Input$SubmitEvent } from "sap/m/Input";
import Table from "sap/m/Table";
import { Button$PressEvent } from "sap/m/Button";
import V4Context from "sap/ui/model/odata/v4/Context";
import { ODataListBinding$DataReceivedEvent } from "sap/ui/model/odata/v4/ODataListBinding";
import MessageBoxHandler from "vamos/cockpit/base/handler/MessageBoxHandler";
import DialogService from "vamos/cockpit/base/services/DialogService";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import StockExtractionDialogController from "vamos/cockpit/data/shopping-cart/controller/StockExtractionDialog.controller";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import { ResponseAssumeToOrderDto } from "vamos/cockpit/model/api/vamos.private.api.types";
import UserHandler from "vamos/cockpit/data/user/handler/UserHandler";

/**
 * @namespace vamos.cockpit.data.shopping-cart
 */

export default class ShoppingCartController extends BaseTypeController {

    public onInit() {
        super.onInit();

        UserHandler.getInstance().getUser()
            .then((user) => {
                const showOrderButton = user.IsAdministrator || user.IsEditor || user.IsClientAdministrator;
                this.viewModel.setProperty("/showOrderButton", showOrderButton);
            }, () => {});
    }

    public onRoutePatternMatched(event: Route$PatternMatchedEvent) {
        super.onRoutePatternMatched(event);

        this.focusInput();
    }

    public onSubmit(oEvent: Input$SubmitEvent) {
        const table: Table = this.byId("tableShoppingCart") as Table;
        const listBinding = table.getBinding("items");

        const input = oEvent.getSource();
        const barcode = input.getValue().trim();

        if (barcode === "") {
            return;
        }

        this.createModelEntry("ShoppingCart", { Barcode: barcode }, listBinding, "$direct");
        input.setValue("");
    }

    public onAddToShoppingCart() {
        const input = this.byId("inputBarcode") as Input;
        input.fireSubmit();
        this.focusInput();
    }

    public onShoppingCartsReceived(event: ODataListBinding$DataReceivedEvent) {
        const iCount = event.getSource().getCount();
        this.viewModel.setProperty("/ShoppingCartCount", iCount);
    }

    public async onRemove(event: Button$PressEvent) {
        const context = event.getSource().getBindingContext() as V4Context;
        await context.delete();
        this.refreshTable();
        this.focusInput();
    }

    private refreshTable() {
        const table: Table = this.byId("tableShoppingCart") as Table;
        table.getBinding("items").refresh();
    }

    private onStepInputChange() {
        this.focusInput();
    }

    private focusInput() {
        const input = this.byId("inputBarcode") as Input;
        input.focus();
    }

    private async assumeToOrder() {
        const response = await ApiService.getInstance().postRestJsonData(`/shopping-cart/assume-to-order`, {});
        const assumeToOrderDto = response.data as ResponseAssumeToOrderDto;
        return assumeToOrderDto.content;
    }

    public async onAssumeToOrder() {
        const result = await this.assumeToOrder();
        this.refreshTable();

        const tree = [
            this.convertSuccessResult(this.getText("CreatedOrders"), { src: "sap-icon://message-success", color: "green" }, result.created),
            this.convertSuccessResult(this.getText("ChangedOrders"), { src: "sap-icon://message-information", color: "#ffcc00" }, result.added),
            {
                text: this.getText("FaultyArticles"),
                iconSrc: "sap-icon://message-error",
                iconColor: "red",
                firstLevel: true,
                nodes: result.error.map(article => {
                    return {
                        text: article,
                    };
                }),
            },
        ];

        this.viewModel.setProperty("/result", tree);
        DialogService.openDialog.call(this, "vamos.cockpit.data.shopping-cart.fragment.AssumeToOrderResultDialog");
    }

    public convertSuccessResult(text: string, icon: { src: string, color: string }, data: Record<string, string[]>) {
        return {
            text: text,
            iconSrc: icon.src,
            iconColor: icon.color,
            firstLevel: true,
            nodes: Object.entries(data).map(([key, value]) => {
                return {
                    text: key,
                    nodes: value.map(value => {
                        return {
                            text: value,
                        };
                    }),
                };
            }),
        };
    }

    public onEmptyShoppingCart() {
        const callbackSuccess = () => {
            void ApiService.getInstance().delete(`/shopping-cart/empty`).then(() => {
                this.refreshTable();
                this.showSuccessMessage(this.getText("EmptiedShoppingCart"));
            });
        };

        MessageBoxHandler.showActionWithOkAndCancelButton(
            this.getText("EmptyShoppingCart"),
            this.getText("EmptyShoppingCartConfirmation"),
            callbackSuccess,
        );
    }

    public onCloseDialog(oEvent: Button$PressEvent) {
        DialogService.onClosePopup(oEvent);
        this.focusInput();
    }

    public async onSwitchToStockExtraction() {
        const view = await ViewHandler.getView("vamos.cockpit.data.shopping-cart.view.StockExtractionDialog", this.getComponent());
        const controller = view.getController() as StockExtractionDialogController;
        controller.openDialog(() => this.refreshTable());
    }
}